import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Scale, ShieldCheck, Copy, Check, RefreshCw, Plus, Trash2, EyeOff,
  UserCheck, CalendarClock, Inbox, Building2, Briefcase,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { api, formatApiError } from '@/lib/api';
import { useApp } from '@/context/AppContext';

const STATUS_CLS = {
  received: 'bg-sky-50 text-sky-700 border-sky-200',
  under_hm_review: 'bg-amber-50 text-amber-700 border-amber-200',
  routed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed_admin: 'bg-slate-100 text-slate-600 border-slate-200',
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(getUiLocale()) : '—');

function StatusBadge({ status, t }) {
  return (
    <Badge variant="outline" className={`text-sm ${STATUS_CLS[status] || 'bg-muted'}`}
           data-testid={`ge-status-badge-${status}`}>
      {t(`globalEthics.status.statuses.${status}`)}
    </Badge>
  );
}

function ReportDetailDialog({ report, onClose, onChanged, t }) {
  const [recipientId, setRecipientId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  if (!report) return null;
  const canAct = ['received', 'under_hm_review'].includes(report.status);
  const act = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl sm:max-w-3xl xl:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="ge-report-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Scale className="h-5 w-5 text-primary" />
            {t('globalEthicsAdmin.detail.title')} {report.reference_number}
            <StatusBadge status={report.status} t={t} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-base">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <p><span className="text-muted-foreground">{t('globalEthicsAdmin.inbox.company')}:</span> <span className="font-medium">{report.company_name}</span></p>
            <p><span className="text-muted-foreground">{t('globalEthicsAdmin.inbox.date')}:</span> {fmt(report.created_at)}</p>
            <p><span className="text-muted-foreground">{t('globalEthicsAdmin.inbox.category')}:</span> {t(`globalEthics.categories.${report.category}`)}</p>
            <p className="flex items-center gap-1.5">
              {report.identity?.provided
                ? <><UserCheck className="h-3.5 w-3.5 text-emerald-600" />{t('globalEthicsAdmin.inbox.identified')}</>
                : <><EyeOff className="h-3.5 w-3.5 text-muted-foreground" />{t('globalEthicsAdmin.inbox.anonymous')}</>}
            </p>
          </div>

          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
              {t('globalEthicsAdmin.detail.description')}
            </p>
            <p className="whitespace-pre-wrap" data-testid="ge-detail-description">{report.description}</p>
          </div>
          {report.additional_info && (
            <div className="rounded-lg border p-3">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
                {t('globalEthicsAdmin.detail.additional')}
              </p>
              <p className="whitespace-pre-wrap">{report.additional_info}</p>
            </div>
          )}

          <div className="rounded-lg border p-3">
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
              {t('globalEthicsAdmin.detail.identity')}
            </p>
            {report.identity?.provided ? (
              <div className="text-sm space-y-0.5" data-testid="ge-detail-identity">
                <p><span className="text-muted-foreground">{t('globalEthicsAdmin.detail.name')}:</span> {report.identity.name || '—'}</p>
                <p><span className="text-muted-foreground">{t('globalEthicsAdmin.detail.contact')}:</span> {report.identity.contact || '—'}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('globalEthicsAdmin.detail.anonymousNote')}</p>
            )}
            {report.meeting_requested && (
              <p className="text-sm mt-2 flex items-start gap-1.5 text-amber-700">
                <CalendarClock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                {t('globalEthicsAdmin.detail.meetingRequested')}
                {!report.identity?.provided && ` ${t('globalEthicsAdmin.detail.meetingAnonWarning')}`}
              </p>
            )}
          </div>

          {report.routing && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-sm" data-testid="ge-detail-routing">
              <p className="font-medium">
                {t('globalEthicsAdmin.detail.routedTo')} {report.routing.recipient?.name} ({report.routing.recipient?.title}) · {report.routing.recipient?.email}
              </p>
              <p className="text-muted-foreground mt-0.5">
                {t('globalEthicsAdmin.detail.routedBy')} {report.routing.routed_by_name} · {t('globalEthicsAdmin.detail.routedAt')} {fmt(report.routing.routed_at)}
              </p>
              {report.routing.note && <p className="mt-1 italic">{report.routing.note}</p>}
            </div>
          )}

          {canAct && (
            <div className="rounded-lg border border-primary/30 p-3 space-y-3" data-testid="ge-route-panel">
              <p className="text-sm font-medium">{t('globalEthicsAdmin.detail.routeTitle')}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{t('globalEthicsAdmin.detail.routeHint')}</p>
              {report.status === 'received' && (
                <Button variant="outline" size="sm" disabled={busy} className="rounded-full"
                        onClick={() => act(() => api.post(`/admin/ethics/reports/${report.id}/start-review`))}
                        data-testid="ge-start-review-btn">
                  {t('globalEthicsAdmin.detail.startReview')}
                </Button>
              )}
              {(report.authorized_recipients || []).length === 0 ? (
                <p className="text-sm text-amber-700">{t('globalEthicsAdmin.detail.noRecipients')}</p>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t('globalEthicsAdmin.detail.recipientLabel')}</Label>
                    <Select value={recipientId} onValueChange={setRecipientId}>
                      <SelectTrigger className="text-base" data-testid="ge-recipient-select">
                        <SelectValue placeholder={t('globalEthicsAdmin.detail.recipientPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {report.authorized_recipients.map((r) => (
                          <SelectItem key={r.id} value={r.id} data-testid={`ge-recipient-${r.id}`}>
                            {r.name} — {r.title} ({r.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm">{t('globalEthicsAdmin.detail.noteLabel')}</Label>
                    <Textarea rows={3} className="min-h-[90px] md:text-base" value={note} onChange={(e) => setNote(e.target.value)}
                              data-testid="ge-route-note-input" />
                  </div>
                  <Button size="sm" className="rounded-full" disabled={!recipientId || busy}
                          onClick={() => act(() => api.post(`/admin/ethics/reports/${report.id}/route`,
                            { recipient_id: recipientId, note: note || null }))}
                          data-testid="ge-route-btn">
                    {t('globalEthicsAdmin.detail.routeBtn')}
                  </Button>
                </>
              )}
            </div>
          )}

          {report.status !== 'closed_admin' && (
            <div className="space-y-1.5">
              <Button variant="outline" size="sm" disabled={busy} className="rounded-full"
                      onClick={() => act(() => api.post(`/admin/ethics/reports/${report.id}/close`))}
                      data-testid="ge-close-btn">
                {t('globalEthicsAdmin.detail.close')}
              </Button>
              <p className="text-sm text-muted-foreground">{t('globalEthicsAdmin.detail.closeHint')}</p>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">
              {t('globalEthicsAdmin.detail.history')}
            </p>
            <ul className="space-y-1.5 text-sm" data-testid="ge-detail-history">
              {(report.status_history || []).map((h, i) => (
                <li key={i} className="flex items-center gap-2">
                  <StatusBadge status={h.status} t={t} />
                  <span className="text-muted-foreground">{fmt(h.at)}{h.by ? ` · ${h.by}` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RecipientsEditor({ value, onSave, saving, t, testPrefix }) {
  const [rows, setRows] = useState(value || []);
  useEffect(() => { setRows(value || []); }, [value]);
  const upd = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const valid = rows.every((r) => (r.name || '').trim().length >= 2
    && (r.title || '').trim().length >= 2 && (r.email || '').includes('@'));
  return (
    <div className="space-y-2" data-testid={`${testPrefix}-recipients-editor`}>
      {rows.map((r, i) => (
        <div key={r.id || i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.5fr_auto] gap-2 items-center">
          <Input value={r.name || ''} className="md:text-base" onChange={(e) => upd(i, 'name', e.target.value)}
                 placeholder={t('globalEthicsAdmin.companies.name')} data-testid={`${testPrefix}-recip-name-${i}`} />
          <Input value={r.title || ''} className="md:text-base" onChange={(e) => upd(i, 'title', e.target.value)}
                 placeholder={t('globalEthicsAdmin.companies.role')} data-testid={`${testPrefix}-recip-title-${i}`} />
          <Input value={r.email || ''} className="md:text-base" onChange={(e) => upd(i, 'email', e.target.value)}
                 placeholder={t('globalEthicsAdmin.companies.email')} data-testid={`${testPrefix}-recip-email-${i}`} />
          <Button variant="ghost" size="icon" onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                  data-testid={`${testPrefix}-recip-remove-${i}`}>
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="rounded-full gap-1.5"
                onClick={() => setRows((r) => [...r, { name: '', title: '', email: '' }])}
                data-testid={`${testPrefix}-recip-add-btn`}>
          <Plus className="h-3.5 w-3.5" /> {t('globalEthicsAdmin.companies.add')}
        </Button>
        <Button size="sm" className="rounded-full" disabled={!valid || saving}
                onClick={() => onSave(rows)} data-testid={`${testPrefix}-recip-save-btn`}>
          {t('globalEthicsAdmin.companies.save')}
        </Button>
      </div>
    </div>
  );
}

function CompanyConfigCard({ cfg, isAdmin, onChanged, t }) {
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(cfg.access_code); } catch { /* noop */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const saveRecipients = async (rows) => {
    setSaving(true);
    try {
      await api.put(`/admin/ethics/companies/${cfg.company_id}/recipients`, { recipients: rows });
      toast.success(t('globalEthicsAdmin.companies.saved'));
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-6 space-y-5" data-testid={`ge-company-card-${cfg.company_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-base font-semibold flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" /> {cfg.company_name}
        </p>
        {isAdmin && (
          <div className="flex items-center gap-2 text-sm">
            <Switch checked={cfg.active} data-testid={`ge-active-switch-${cfg.company_id}`}
                    onCheckedChange={async (v) => {
                      try { await api.post(`/admin/ethics/companies/${cfg.company_id}/active`, { active: v }); onChanged(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }} />
            {t('globalEthicsAdmin.companies.active')}
          </div>
        )}
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(320px,400px)_1fr] items-start">
        <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wider">{t('globalEthicsAdmin.companies.codeLabel')}</p>
            <p className="font-mono text-xl font-semibold tracking-[0.15em]" data-testid={`ge-code-${cfg.company_id}`}>{cfg.access_code}</p>
            <p className="text-sm text-muted-foreground mt-1 break-all">
              {t('globalEthicsAdmin.companies.publicUrl')}: {window.location.origin}/ethics
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={copy}
                    data-testid={`ge-copy-code-${cfg.company_id}`}>
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t('globalEthicsAdmin.companies.copied') : t('globalEthicsAdmin.companies.copy')}
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" className="rounded-full gap-1.5"
                      data-testid={`ge-regenerate-${cfg.company_id}`}
                      onClick={async () => {
                        if (!window.confirm(t('globalEthicsAdmin.companies.regenerateConfirm'))) return;
                        try { await api.post(`/admin/ethics/companies/${cfg.company_id}/regenerate-code`); onChanged(); }
                        catch (e) { toast.error(formatApiError(e)); }
                      }}>
                <RefreshCw className="h-3.5 w-3.5" /> {t('globalEthicsAdmin.companies.regenerate')}
              </Button>
            )}
          </div>
        </div>
        <div>
          <p className="text-base font-medium">{t('globalEthicsAdmin.companies.recipientsTitle')}</p>
          <p className="text-sm text-muted-foreground mb-3">{t('globalEthicsAdmin.companies.recipientsHint')}</p>
          <RecipientsEditor value={cfg.authorized_recipients} onSave={saveRecipients}
                            saving={saving} t={t} testPrefix={`ge-admin-${cfg.company_id}`} />
        </div>
      </div>
    </Card>
  );
}

export default function AdminGlobalEthics() {
  const { t } = useTranslation();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';
  const [reports, setReports] = useState([]);
  const [companies, setCompanies] = useState({ items: [], available_companies: [] });
  const [services, setServices] = useState([]);
  const [detail, setDetail] = useState(null);
  const [newCompanyId, setNewCompanyId] = useState('');

  const load = useCallback(async () => {
    try {
      const [r, c, s] = await Promise.all([
        api.get('/admin/ethics/reports'),
        api.get('/admin/ethics/companies'),
        api.get('/admin/ethics/service-requests'),
      ]);
      setReports(r.data.items || []);
      setCompanies(c.data || {});
      setServices(s.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (rid) => {
    try {
      const { data } = await api.get(`/admin/ethics/reports/${rid}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const counts = useMemo(() => ({
    open: reports.filter((r) => ['received', 'under_hm_review'].includes(r.status)).length,
  }), [reports]);

  return (
    <div data-testid="admin-global-ethics-page">
      <Topbar title={t('globalEthicsAdmin.title')} />
      <div className="p-6 lg:p-8 max-w-[1440px] mx-auto space-y-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-2xl bg-primary flex items-center justify-center shrink-0">
            <ShieldCheck className="h-6 w-6 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('globalEthicsAdmin.title')}</h1>
            <p className="text-base text-muted-foreground leading-relaxed mt-0.5">{t('globalEthicsAdmin.subtitle')}</p>
          </div>
        </div>

        <Tabs defaultValue="inbox">
          <TabsList>
            <TabsTrigger value="inbox" className="gap-1.5" data-testid="ge-tab-inbox">
              <Inbox className="h-3.5 w-3.5" /> {t('globalEthicsAdmin.tabs.inbox')}
              {counts.open > 0 && <Badge className="text-xs px-1.5">{counts.open}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="companies" className="gap-1.5" data-testid="ge-tab-companies">
              <Building2 className="h-3.5 w-3.5" /> {t('globalEthicsAdmin.tabs.companies')}
            </TabsTrigger>
            <TabsTrigger value="services" className="gap-1.5" data-testid="ge-tab-services">
              <Briefcase className="h-3.5 w-3.5" /> {t('globalEthicsAdmin.tabs.services')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inbox" className="mt-4">
            {reports.length === 0 ? (
              <Card className="p-12 text-center" data-testid="ge-inbox-empty">
                <Inbox className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-base text-muted-foreground">{t('globalEthicsAdmin.inbox.empty')}</p>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="ge-inbox-list">
                {reports.map((r) => (
                  <Card key={r.id} className="p-5 flex items-center gap-4 flex-wrap cursor-pointer hover:border-primary/40 transition-colors"
                        onClick={() => openDetail(r.id)} data-testid={`ge-report-row-${r.reference_number}`}>
                    <p className="font-mono text-base font-semibold">{r.reference_number}</p>
                    <p className="text-base">{r.company_name}</p>
                    <Badge variant="outline" className="text-sm">{t(`globalEthics.categories.${r.category}`)}</Badge>
                    <span className="text-sm text-muted-foreground inline-flex items-center gap-1.5">
                      {r.identity?.provided
                        ? <><UserCheck className="h-4 w-4" />{t('globalEthicsAdmin.inbox.identified')}</>
                        : <><EyeOff className="h-4 w-4" />{t('globalEthicsAdmin.inbox.anonymous')}</>}
                    </span>
                    {r.meeting_requested && (
                      <span className="text-sm text-amber-700 inline-flex items-center gap-1.5">
                        <CalendarClock className="h-4 w-4" />{t('globalEthicsAdmin.inbox.meetingYes')}
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">{fmt(r.created_at)}</span>
                      <StatusBadge status={r.status} t={t} />
                    </span>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="companies" className="mt-4 space-y-4">
            {isAdmin && (companies.available_companies || []).length > 0 && (
              <Card className="p-4 flex items-center gap-3 flex-wrap" data-testid="ge-enable-company-card">
                <p className="text-sm font-medium">{t('globalEthicsAdmin.companies.enableTitle')}</p>
                <Select value={newCompanyId} onValueChange={setNewCompanyId}>
                  <SelectTrigger className="w-64" data-testid="ge-enable-company-select">
                    <SelectValue placeholder={t('globalEthicsAdmin.companies.selectCompany')} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.available_companies.map((c) => (
                      <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="rounded-full" disabled={!newCompanyId}
                        onClick={async () => {
                          try { await api.post('/admin/ethics/companies', { company_id: newCompanyId }); setNewCompanyId(''); load(); }
                          catch (e) { toast.error(formatApiError(e)); }
                        }}
                        data-testid="ge-enable-company-btn">
                  {t('globalEthicsAdmin.companies.enableCta')}
                </Button>
              </Card>
            )}
            {(companies.items || []).length === 0 ? (
              <Card className="p-12 text-center">
                <Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-base text-muted-foreground">{t('globalEthicsAdmin.companies.empty')}</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {companies.items.map((cfg) => (
                  <CompanyConfigCard key={cfg.company_id} cfg={cfg} isAdmin={isAdmin} onChanged={load} t={t} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="services" className="mt-4">
            {services.length === 0 ? (
              <Card className="p-12 text-center" data-testid="ge-services-empty">
                <Briefcase className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-base text-muted-foreground">{t('globalEthicsAdmin.services.empty')}</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {services.map((s) => (
                  <Card key={s.id} className="p-5 flex items-center gap-4 flex-wrap" data-testid={`ge-service-row-${s.id}`}>
                    <p className="text-base font-medium">{t(`globalEthicsAdmin.services.types.${s.type}`)}</p>
                    <p className="text-sm text-muted-foreground">{s.company_name} · {s.requested_by_name}</p>
                    {s.note && <p className="text-sm text-muted-foreground italic w-full">{s.note}</p>}
                    <span className="ml-auto flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">{fmt(s.created_at)}</span>
                      <Select value={s.status} onValueChange={async (v) => {
                        try { await api.post(`/admin/ethics/service-requests/${s.id}/status`, { status: v }); load(); }
                        catch (e) { toast.error(formatApiError(e)); }
                      }}>
                        <SelectTrigger className="h-9 w-40 text-sm" data-testid={`ge-service-status-${s.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['new', 'contacted', 'closed'].map((st) => (
                            <SelectItem key={st} value={st}>{t(`globalEthicsAdmin.services.statuses.${st}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </span>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      {detail && (
        <ReportDetailDialog report={detail} t={t}
                            onClose={() => setDetail(null)}
                            onChanged={() => { openDetail(detail.id); load(); }} />
      )}
    </div>
  );
}
